/**
 * Where each screen sits, for the breadcrumb trail.
 *
 * The portal's routes are almost all flat - "/quote-lost-report" rather than
 * "/reports/quote-lost" - so the path cannot be split into a trail on its own.
 * This is the map of what lives under what.
 *
 * A parent is only set where the user can genuinely get to that screen from it:
 * the Reports and Management hubs list their children as tiles, so those are
 * real relationships. Screens reachable only from the sidebar sit at the top
 * level, and a trail of "Home > Management > Sites" would be a lie when
 * Management has no Sites tile.
 */
export interface BreadcrumbRoute {
  /** What the crumb reads. */
  label: string
  /** The path of the screen above this one, or undefined for a top level screen. */
  parent?: string
}

export const HOME_PATH = '/'

export const BREADCRUMB_ROUTES: Record<string, BreadcrumbRoute> = {
  // Top level - everything the sidebar goes to directly
  '/ai': { label: 'AI Assistant' },
  '/contacts': { label: 'Contacts' },
  '/diary': { label: 'Diary' },
  '/enquiries': { label: 'Enquiries' },
  '/go-no-go': { label: 'Go/No-Go' },
  '/invoicing': { label: 'Sales' },
  '/uniform': { label: 'Uniform' },
  '/invoicing/new': { label: 'New invoice', parent: '/invoicing' },
  '/invoicing/:invoiceId': { label: 'Invoice', parent: '/invoicing' },
  '/jobs': { label: 'Jobs' },
  '/notifiable-works': { label: 'Notifiable Works' },
  '/management': { label: 'Management' },
  '/notes': { label: 'Notes' },
  '/pieces': { label: 'Pieces' },
  '/purchase-orders': { label: 'Purchase Orders' },
  '/search': { label: 'Search results' },
  '/quotes': { label: 'Quotes' },
  '/reports': { label: 'Reports' },
  '/service-reminders': { label: 'Service Reminders' },

  // Administrator screens the sidebar reaches but the Management hub does not
  // list, so they stay one level down from home rather than claiming a parent
  // that has no tile for them.
  '/clients': { label: 'Clients' },
  '/projects': { label: 'Projects' },
  '/quote-templates': { label: 'Quote Templates' },
  '/sites': { label: 'Sites' },

  // Reports - every tile on the Reports hub
  '/employee-open-notes': { label: 'Employee Open Notes', parent: '/reports' },
  '/employee-time-report': { label: 'Employee Time', parent: '/reports' },
  '/invoices-report': { label: 'Invoices', parent: '/reports' },
  '/material-movements': { label: 'Material Movements', parent: '/reports' },
  '/pipeline': { label: 'Pipeline', parent: '/reports' },
  '/completed-jobs': { label: 'Completed Jobs', parent: '/reports' },
  '/quote-conversion-report': { label: 'Quote Conversion', parent: '/reports' },
  '/quote-job-summary': { label: 'Quote & Job Summary', parent: '/reports' },
  '/quote-lost-report': { label: 'Quote Lost', parent: '/reports' },
  '/report-schedules': { label: 'Report Schedules', parent: '/reports' },

  // Management - every tile on the Management hub
  '/accounts': { label: 'Accounts', parent: '/management' },
  '/database-explorer': { label: 'Database Explorer', parent: '/management' },
  '/invoice-descriptions': { label: 'Invoice Descriptions', parent: '/management' },
  '/management/forms': { label: 'Forms', parent: '/management' },
  '/management/groups': { label: 'Groups', parent: '/management' },
  '/management/links': { label: 'Links', parent: '/management' },
  '/management/notifications': { label: 'Notifications', parent: '/management' },
  '/text-messages': { label: 'Text Messages', parent: '/management' },
  '/timesheets': { label: 'Timesheets', parent: '/management' },
  '/tools': { label: 'Tools', parent: '/management' },
  '/users': { label: 'Users', parent: '/management' },
  '/vehicles': { label: 'Vehicles', parent: '/management' },
  '/worktypes': { label: 'Worktypes', parent: '/management' },

  // Screens reached from within another screen
  '/diary-old': { label: 'Diary (Classic)', parent: '/diary' },
  '/job-costing': { label: 'Job Costing', parent: '/jobs' },
  '/jobs-kendo': { label: 'Jobs', parent: '/jobs' },
  '/jobs-optimized': { label: 'Jobs', parent: '/jobs' },
  '/enquiries/:contactNumber': { label: 'Enquiry', parent: '/enquiries' },
  '/jobs/:jobId/financial-statement': { label: 'Financial Statement', parent: '/jobs' },
  '/management/announcements': { label: 'Announcements', parent: '/management' },
  '/management/piggy-bank': { label: 'Piggy Bank', parent: '/management' },
  '/management/forms/responses/:formId': { label: 'Responses', parent: '/management/forms' },
  '/service-reminders/add': { label: 'Add Reminder', parent: '/service-reminders' },
  '/incoming/:phoneNumber': { label: 'Incoming Call' },
}

/**
 * The registry entry for a path, matching a route with parameters in it by
 * shape rather than by text.
 */
export const matchBreadcrumbRoute = (
  pathname: string
): { path: string; route: BreadcrumbRoute } | null => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname

  const exact = BREADCRUMB_ROUTES[path]
  if (exact) {
    return { path, route: exact }
  }

  const segments = path.split('/')

  // Only patterned routes are worth trying, and only those with the same number
  // of segments - ":jobId" matches one segment, never several.
  for (const [candidate, route] of Object.entries(BREADCRUMB_ROUTES)) {
    if (!candidate.includes(':')) continue

    const candidateSegments = candidate.split('/')
    if (candidateSegments.length !== segments.length) continue

    const matches = candidateSegments.every(
      (segment, i) => segment.startsWith(':') || segment === segments[i]
    )

    if (matches) {
      // The live path is returned, not the pattern, so the crumb links back to
      // the record actually being looked at.
      return { path, route }
    }
  }

  return null
}

export interface Crumb {
  label: string
  path: string
}

/**
 * The trail for a path: nearest screen last, its ancestors before it. Home is
 * not included - the component draws that as its icon.
 */
export const buildBreadcrumbs = (pathname: string): Crumb[] => {
  const crumbs: Crumb[] = []
  let current = matchBreadcrumbRoute(pathname)
  // A registry that somehow pointed at itself would otherwise spin forever.
  const seen = new Set<string>()

  while (current && !seen.has(current.path)) {
    seen.add(current.path)
    crumbs.unshift({ label: current.route.label, path: current.path })
    current = current.route.parent ? matchBreadcrumbRoute(current.route.parent) : null
  }

  return crumbs
}
